import { flatten, xor } from "lodash-es";

import type { DictsysLemma } from "@/types";

type language = "da" | "de";

export interface HeaderLemma {
  type: "lemma" | "alternative";
  lemma: DictsysLemma;
  info: HeaderCommonInfo;
}

export class HeaderCommonInfo {
  constructor(
    readonly irregular: string | undefined,
    readonly grammaticalComment: string | undefined,
    readonly categories: string[],
    readonly varieties: string[],
    readonly usages: string[]
  ) {}

  getFiltered(): string[][] {
    return [
      [this.irregular ?? ""],
      [this.grammaticalComment ?? ""],
      this.categories,
      this.varieties,
      this.usages
    ].filter((element) => element.length > 0 && element.every((elementItem) => elementItem !== ""));
  }

  getFilteredFlattened(): string[] {
    return flatten(this.getFiltered());
  }

  isEmpty(): boolean {
    return this.getFilteredFlattened().length === 0;
  }
}

export function getHeaderCommonInfo(lemmas: DictsysLemma[], language: language): HeaderCommonInfo {
  return new HeaderCommonInfo(
    getCommonSingleValue(lemmas, (lemma) => [getIrregularText(lemma, language)]),
    getCommonSingleValue(lemmas, (lemma) => [lemma.grammaticalComment]),
    getCommonArrayValues(lemmas, (lemma) => lemma.metadata.categories),
    getCommonArrayValues(lemmas, (lemma) => lemma.metadata.varieties),
    getCommonArrayValues(lemmas, (lemma) => lemma.metadata.usages)
  );
}

export function getHeaderLemmas(
  commonInfo: HeaderCommonInfo,
  lemmas: DictsysLemma[],
  language: language
): HeaderLemma[] {
  return lemmas.map((lemma, index) => ({
    type: index === 0 ? "lemma" : "alternative",
    lemma,
    info: new HeaderCommonInfo(
      ignoreCommonInfoSingleValue(getIrregularText(lemma, language), commonInfo.irregular),
      ignoreCommonInfoSingleValue(lemma.grammaticalComment, commonInfo.grammaticalComment),
      ignoreCommonInfoArrayValues(lemma.metadata.categories, commonInfo.categories),
      ignoreCommonInfoArrayValues(lemma.metadata.varieties, commonInfo.varieties),
      ignoreCommonInfoArrayValues(lemma.metadata.usages, commonInfo.usages)
    )
  }));
}

const getIrregularText = (lemma: DictsysLemma, language: language): string =>
  lemma.metadata.irregular ? (language === "da" ? "uregelmæssig" : "irregulär") : "";

const ignoreCommonInfoSingleValue = (specific: string, info?: string): string | undefined =>
  info === undefined ? specific : undefined;

const ignoreCommonInfoArrayValues = (specific: string[], info: string[]): string[] =>
  info.length === 0 ? specific : [];

const getCommonSingleValue = (lemmas: DictsysLemma[], mapFunc: (lemma: DictsysLemma) => string[]): string | undefined =>
  getCommonArrayValues(lemmas, mapFunc)[0];

const getCommonArrayValues = (lemmas: DictsysLemma[], mapFunc: (lemma: DictsysLemma) => string[]): string[] => {
  if (lemmas.length === 1 || xor(...lemmas.map(mapFunc)).length === 0) {
    return mapFunc(lemmas[0]);
  }

  return [];
};
